import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from three different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 * 3. Subscription widget (plan selection changes)
 */
class ProductPrice extends HTMLElement {
  /** @type {string | null} Original price HTML for restoring after subscription toggle */
  #originalPriceHTML = null;

  /** @type {boolean} Whether the container currently shows a subscription override (vs the original one-time HTML) */
  #showingSubscription = false;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('subscription-widget:plan-change', this.#handlePlanChange);
    this.#storeOriginalPrice();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('subscription-widget:plan-change', this.#handlePlanChange);
  }

  /** Stores the current price HTML so it can be restored when switching back to one-time purchase. */
  #storeOriginalPrice() {
    const container = this.querySelector('[ref="priceContainer"]');
    if (container) this.#originalPriceHTML = container.innerHTML;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }

    // After a variant swap the container has fresh server-rendered one-time HTML.
    // Re-store it as the original and reset the override flag so the next
    // plan-change event re-captures from the correct baseline.
    this.#storeOriginalPrice();
    this.#showingSubscription = false;
  };

  /**
   * Handles subscription/onetime plan changes from the subscription widget.
   * Updates the displayed price to reflect the active purchase option.
   *
   * Only captures the original (one-time) HTML when *transitioning into*
   * the subscription state — not on every subsequent subscription event.
   * Re-capturing while already showing a subscription override would
   * pollute `#originalPriceHTML` with the subscription price itself, so
   * the eventual switch back to one-time would restore the wrong value.
   * Variant updates explicitly reset `#showingSubscription` in `updatePrice`,
   * so a fresh server-rendered one-time price is always captured after a
   * variant swap.
   *
   * @param {CustomEvent} event - The plan change event with priceHTML in detail.
   */
  #handlePlanChange = (event) => {
    const { type, priceHTML } = event.detail;
    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    if (type === 'subscription' && priceHTML) {
      if (!this.#showingSubscription) {
        this.#storeOriginalPrice();
        this.#showingSubscription = true;
      }
      container.innerHTML = priceHTML;
    } else if (this.#originalPriceHTML != null) {
      container.innerHTML = this.#originalPriceHTML;
      this.#showingSubscription = false;
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
